import _ from 'lodash'
import React, { useCallback, useEffect, useState } from 'react'
import { FieldHookConfig } from 'formik'
import { useField } from '@/hooks/useField'

import AutocompleteInput, { AutocompleteInputProps, AutocompleteValue } from '@/components/ui/inputs/AutocompleteInput'
import ErrorMessage from '@/components/formik/messages/ErrorMessage'

export type AutocompleteFieldProps = Omit<AutocompleteInputProps, 'onChange'> & FieldHookConfig<string | string[] | null> & {
  disableHelperText?: boolean
}

const MemoizedAutocompleteInput = React.memo(AutocompleteInput)

export function AutocompleteField(props: AutocompleteFieldProps) {
  const { multiple, name, onInputChange, options, disableHelperText, loading, ...rest } = props
  const [field, meta, helpers] = useField<string | string[] | null>(name)

  const [inputValue, setInputValue] = useState<string>('')
  const [value, setValue] = useState<AutocompleteValue>(multiple ? [] : null)

  useEffect(() => {
    if (meta.initialValue === meta.value && options.length) {
      setValue(multiple ? _.filter(options, option => _.includes(meta.initialValue, option.value)) : (_.find(options, option => option.value === meta.value) || null))
    }
  }, [meta.initialValue, meta.value, multiple, options])

  useEffect(() => {
    if (value === null) setInputValue('')
  }, [value])

  const handleChange = useCallback((newValue: AutocompleteValue) => {
    setValue(newValue)
    helpers.setValue(Array.isArray(newValue) ? _.map(newValue, 'value') : (newValue?.value || ''))
  }, [helpers])

  const handleInputChange = useCallback((newInputValue: string, reason: string) => {
    setInputValue(newInputValue)
    if (reason === 'input') onInputChange?.(newInputValue, reason)
  }, [onInputChange])

  return (
    <MemoizedAutocompleteInput
      {...rest}
      error={!!meta.error && meta.touched}
      helperText={!!meta.error && meta.touched && !disableHelperText && (
        <ErrorMessage error={meta.error} />
      )}
      inputValue={inputValue}
      multiple={multiple}
      name={field.name}
      options={options}
      loading={loading}
      onBlur={field.onBlur}
      onChange={handleChange}
      onInputChange={handleInputChange}
      value={value}
    />
  )
}

export default AutocompleteField